import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type { MultiPolygon, Polygon } from 'geojson';
import { environment } from '../../../environments/environment';
import { retryTransient } from '../api/request-retry';

/**
 * When the drive-time area is measured: a Wednesday midday, away from both
 * rush hours. Pinned so the same address returns the same area whenever anyone
 * asks - live traffic swung one Miami Beach area by ~9% across a day.
 *
 * Mapbox reads only the weekday and time and discards the date, so this never
 * goes stale; it must NOT come from `new Date()`, which would put the run time
 * back into the answer. `YYYY-MM-DDThh:mm` in the address's local time; a `Z`
 * or `±hh:mm` offset is rejected with a 422.
 */
export const ISOCHRONE_DEPART_AT = '2026-08-26T12:00';

interface MapboxIsochroneResponse {
  features: { geometry: Polygon | MultiPolygon }[];
}

/**
 * Drive-time areas from the Mapbox Isochrone API, shared by the sales-forecast
 * pages and the main map's trade-area action so both describe the same polygon
 * for the same address.
 */
@Injectable({
  providedIn: 'root',
})
export class IsochroneService {
  constructor(private http: HttpClient) {}

  /**
   * Null means Mapbox answered with no polygon: some addresses have no
   * routable roads. A failed request errors instead, so callers can tell
   * "nothing here" apart from an outage or an expired token.
   */
  public fetchDriveTimeArea(
    lng: number,
    lat: number,
    driveMinutes: number
  ): Observable<Polygon | MultiPolygon | null> {
    // driving-traffic rather than driving: billed the same, but it models
    // typical traffic instead of posted speed limits.
    const url =
      `https://api.mapbox.com/isochrone/v1/mapbox/driving-traffic/${lng},${lat}` +
      `?contours_minutes=${driveMinutes}&polygons=true` +
      `&depart_at=${ISOCHRONE_DEPART_AT}` +
      `&access_token=${environment.mapbox.accessToken}`;

    return this.http.get<MapboxIsochroneResponse>(url).pipe(
      retryTransient('mapbox isochrone'),
      map((res) => {
        const geometry = res.features?.[0]?.geometry ?? null;
        if (!geometry) {
          // A breadcrumb rather than an issue: not an error.
          console.warn('Isochrone returned no polygon');
        }
        return geometry;
      })
    );
  }
}
